import { Heading, VStack, HStack, Box, Flex, Text, Icon, Image, SimpleGrid } from "@chakra-ui/react";
import FullScreenSection from "./FullScreenSection";
import { CheckCircleIcon, LockIcon } from "@chakra-ui/icons";
import { FaShieldAlt, FaChartLine, FaGlobe, FaHeart, FaUsers, FaLightbulb } from "react-icons/fa";
import JoinCampaign from "./JoinCampaign";

const coreValues = [
  {
    icon: FaHeart,
    color: "red.500",
    title: "Customer First",
    description: "We prioritize our customers' needs and strive to deliver exceptional service that exceeds expectations at every touchpoint."
  },
  {
    icon: FaLightbulb,
    color: "yellow.500",
    title: "Innovation",
    description: "We leverage cutting-edge technology and AI to continuously improve our platform and provide smarter financial solutions."
  },
  {
    icon: FaShieldAlt,
    color: "black.500",
    title: "Trust & Transparency",
    description: "We believe in complete transparency in our pricing and operations, building trust through honest communication."
  },
  {
    icon: FaUsers,
    color: "teal.500",
    title: "Community",
    description: "We uplift the communities we serve through partnerships, education, and charity initiatives that create lasting impact."
  }
];

const teamMembers = [
  {
    name: "Anthony Diyokeugwu",
    role: "CEO & Co-Founder",
    image: "",
    bio: "Canada"
  },
  {
    name: "Precious Chinenye",
    role: "Head of Compliance",
    image: "",
    bio: "Global"
  },
  {
    name: "Amara Okafor",
    role: "Head of Operations",
    image: "",
    bio: "Canada & Nigeria"
  },
  {
    name: "David Martinez",
    role: "Chief Security Officer",
    image: "",
    bio: "USA"
  }
];

const AboutUs = () => {
  return (
    <VStack spacing={0} width="100%">
      {/* Hero Section - Vision & Mission */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundImage={`url(${require('../images/landingPageBgFlipped.png')})`}
        backgroundSize="cover"
        backgroundPosition="center center"
        backgroundRepeat="no-repeat"
        py={100}
        overlay={true}
        overlayColor="whiteAlpha.800"
      >
        <VStack spacing={8} maxW="1200px" textAlign="center" zIndex={10}>
          <Heading fontSize={{ base: "2.4rem", md: "3.2rem", lg: "4rem" }} fontWeight="bold" color="black">
            About Swiftchang
          </Heading>
          <Heading fontSize={{ base: "1.4rem", md: "1.8rem", lg: "2rem" }} fontWeight="medium" color="gray.700" maxW="900px">
            Our Vision & Mission
          </Heading>
          <Text fontSize={{ base: "1rem", md: "1.1rem", lg: "1.3rem" }} color="gray.600" maxW="1000px" lineHeight="1.8">
            We're on a mission to revolutionize cross-border payments and transfers for immigrants and travelers worldwide. 
            Our vision is to create a world where sending money across borders is as simple, affordable, and transparent as 
            a local bank transfer—empowering millions to support their loved ones and manage their finances globally without barriers.
          </Text>
          
          {/* Hero Images */}
          <HStack spacing={6} mt={8} flexWrap="wrap" justifyContent="center">
            <Image 
              src="https://images.unsplash.com/photo-1556157382-97eda2d62296?w=300&h=300&fit=crop" 
              alt="Happy customer" 
              boxSize={{ base: "140px", md: "180px", lg: "200px" }} 
              borderRadius="xl" 
              objectFit="cover"
              boxShadow="xl"
            />
            <Image 
              src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=300&h=300&fit=crop" 
              alt="Diverse team" 
              boxSize={{ base: "140px", md: "180px", lg: "200px" }} 
              borderRadius="xl" 
              objectFit="cover"
              boxShadow="xl"
            />
            <Image 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=300&h=300&fit=crop" 
              alt="Customer service" 
              boxSize={{ base: "140px", md: "180px", lg: "200px" }} 
              borderRadius="xl" 
              objectFit="cover"
              boxShadow="xl"
            />
          </HStack>
        </VStack>
      </FullScreenSection>

      {/* The Challenge Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="gray.50"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaGlobe} boxSize={{ base: 12, md: 16 }} color="black.500" />
            <Heading fontSize={{ base: "2rem", md: "2.5rem", lg: "3rem" }} fontWeight="bold">
              The Growing Need
            </Heading>
            <Text fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }} color="gray.600" maxW="900px" lineHeight="1.8">
              In today's interconnected world, millions of immigrants and travelers face significant challenges when managing 
              their finances across borders. Traditional banking systems charge exorbitant fees, offer poor exchange rates, 
              and lack transparency—making it expensive and complicated to send money home or manage multiple currencies.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} width="100%">
            <Box p={8} bg="white" borderRadius="xl" boxShadow="lg">
              <Heading fontSize="1.5rem" mb={4} color="red.600">The Problem</Heading>
              <VStack align="flex-start" spacing={3}>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">High transfer fees eating into hard-earned money</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Hidden charges and poor exchange rates</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Lack of multi-currency wallet solutions</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Uncertain timing and fluctuating rates</Text>
                </HStack>
                <HStack>
                  <Box w="8px" h="8px" bg="red.500" borderRadius="full" />
                  <Text color="gray.700">Security concerns with online transfers</Text>
                </HStack>
              </VStack>
            </Box>

            <Box p={8} bg="white" borderRadius="xl" boxShadow="lg">
              <Heading fontSize="1.5rem" mb={4} color="green.600">Our Solution</Heading>
              <VStack align="flex-start" spacing={3}>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">Absolutely zero transfer fees</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">Transparent, real-time exchange rates</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">Multi-currency wallets for easy management</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">AI-powered optimal transfer timing</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text color="gray.700" fontWeight="semibold">Bank-level security with TLS encryption</Text>
                </HStack>
              </VStack>
            </Box>
          </SimpleGrid>
        </VStack>
      </FullScreenSection>

      {/* Zero Fees & Best Value Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="white"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <Heading fontSize={{ base: "2rem", md: "2.5rem", lg: "3rem" }} fontWeight="bold" textAlign="center">
            Get the Most Value for Your Money
          </Heading>
          
          <Flex gap={12} alignItems="center" flexDirection={{ base: "column", md: "row" }}>
            <Image 
              src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=500&h=400&fit=crop" 
              alt="Happy family" 
              borderRadius="xl" 
              boxShadow="2xl"
              flex="1"
            />
            <VStack align="flex-start" spacing={6} flex="1">
              <Box>
                <Heading fontSize={{ base: "1.4rem", md: "1.7rem", lg: "2rem" }} mb={3} color="blue.600">Zero Fees, Always</Heading>
                <Text fontSize={{ base: "0.95rem", md: "1rem", lg: "1.1rem" }} color="gray.600" lineHeight="1.8">
                  We believe every dollar you send should reach its destination. That's why we charge zero transfer fees—no 
                  hidden charges, no surprises. What you see is what you get, ensuring maximum value for your hard-earned money.
                </Text>
              </Box>
              
              <Box>
                <Heading fontSize={{ base: "1.4rem", md: "1.7rem", lg: "2rem" }} mb={3} color="purple.600">Real Forex Market Rates</Heading>
                <Text fontSize={{ base: "0.95rem", md: "1rem", lg: "1.1rem" }} color="gray.600" lineHeight="1.8">
                  Our exchange rates reflect the current forex market in real-time. We provide complete transparency—you'll 
                  always know exactly what rate you're getting, with no markup or hidden margins. We pull live rates from 
                  global financial markets, ensuring you get fair, competitive prices every single time.
                </Text>
              </Box>
            </VStack>
          </Flex>
        </VStack>
      </FullScreenSection>

      {/* AI-Powered Features Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="blue.50"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaChartLine} boxSize={16} color="black.600" />
            <Heading fontSize="3rem" fontWeight="bold">
              AI-Powered Intelligence
            </Heading>
            <Text fontSize="1.2rem" color="gray.600" maxW="900px" lineHeight="1.8">
              Leveraging advanced artificial intelligence to help you make smarter financial decisions
            </Text>
          </VStack>

          <Flex gap={12} alignItems="center" flexDirection={{ base: "column", md: "row" }}>
            <VStack align="flex-start" spacing={6} flex="1">
              <Box p={6} bg="white" borderRadius="xl" boxShadow="lg" width="100%">
                <Heading fontSize="1.5rem" mb={3} color="black.600">Trend Prediction</Heading>
                <Text color="gray.600" lineHeight="1.8">
                  Our AI algorithms analyze historical data, market indicators, and global economic trends to predict 
                  currency movements. This helps you understand when rates might be more favorable for your transfers.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="xl" boxShadow="lg" width="100%">
                <Heading fontSize="1.5rem" mb={3} color="blue.500">Optimal Transfer Windows</Heading>
                <Text color="gray.600" lineHeight="1.8">
                  Get personalized notifications about the best times to transfer money based on AI predictions. Our system 
                  monitors exchange rate fluctuations 24/7 and alerts you when conditions are optimal, helping you maximize 
                  the value of every transfer.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="xl" boxShadow="lg" width="100%">
                <Heading fontSize="1.5rem" mb={3} color="green.600">Smart Recommendations</Heading>
                <Text color="gray.600" lineHeight="1.8">
                  Our AI learns from your transfer patterns and provides intelligent recommendations for currency management, 
                  helping you save money and make informed decisions about when to exchange currencies in your multi-currency wallet.
                </Text>
              </Box>
            </VStack>

           {/* <Image 
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=600&fit=crop" 
              alt="AI technology" 
              borderRadius="xl" 
              boxShadow="2xl"
              flex="1"
            /> */}
          </Flex>
        </VStack>
      </FullScreenSection>

      {/* Security Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="gray.50"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <VStack spacing={4} textAlign="center">
            <Icon as={LockIcon} boxSize={16} color="black.600" />
            <Heading fontSize="3rem" fontWeight="bold">
              Your Security, Our Priority
            </Heading>
            <Text fontSize="1.2rem" color="gray.600" maxW="900px" lineHeight="1.8">
              We understand that trust is paramount when it comes to your money. That's why we've implemented 
              bank-level security measures to protect your data and transactions.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} width="100%">
            <Box p={8} bg="white" borderRadius="xl" boxShadow="lg" border="2px solid" borderColor="green.200">
              <Icon as={FaShieldAlt} boxSize={12} color="green.600" mb={4} />
              <Heading fontSize="1.8rem" mb={4}>TLS Encryption Protocol</Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                All data transmitted through our platform is secured using Transport Layer Security (TLS) 1.3—the same 
                military-grade encryption used by major banks worldwide. This ensures that your personal information, 
                financial data, and transaction details are protected from unauthorized access.
              </Text>
              <VStack align="flex-start" spacing={2}>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text fontSize="sm" color="gray.700">End-to-end encryption for all communications</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text fontSize="sm" color="gray.700">Secure data storage with advanced encryption</Text>
                </HStack>
                <HStack>
                  <CheckCircleIcon color="green.500" />
                  <Text fontSize="sm" color="gray.700">Regular security audits and penetration testing</Text>
                </HStack>
              </VStack>
            </Box>

            <Box p={8} bg="white" borderRadius="xl" boxShadow="lg" border="2px solid" borderColor="black.200">
              <Icon as={LockIcon} boxSize={12} color="black.600" mb={4} />
              <Heading fontSize="1.8rem" mb={4}>Additional Security Measures</Heading>
              <VStack align="flex-start" spacing={4}>
                <Box>
                  <Text fontWeight="bold" color="gray.700" mb={1}>Two-Factor Authentication (2FA)</Text>
                  <Text color="gray.600" fontSize="sm">Extra layer of protection for your account access</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="gray.700" mb={1}>Fraud Detection AI</Text>
                  <Text color="gray.600" fontSize="sm">Real-time monitoring for suspicious activities</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="gray.700" mb={1}>Regulatory Compliance</Text>
                  <Text color="gray.600" fontSize="sm">Fully compliant with international financial regulations</Text>
                </Box>
                {/*<Box>
                  <Text fontWeight="bold" color="gray.700" mb={1}>Secure Money Storage</Text>
                  <Text color="gray.600" fontSize="sm">Your funds are held in segregated accounts with partner banks</Text>
                </Box>*/}
              </VStack>
            </Box>
          </SimpleGrid>

          <Box p={6} bg="green.100" borderRadius="xl" maxW="800px" textAlign="center">
            <Text fontSize="1.1rem" color="green.900" fontWeight="semibold">
              Your peace of mind is our commitment. We continuously invest in the latest security technologies 
              to ensure your money and data remain safe at all times.
            </Text>
          </Box>
        </VStack>
      </FullScreenSection>

      {/* Core Values Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="white"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <Heading fontSize="3rem" fontWeight="bold" textAlign="center">
            Our Core Values
          </Heading>
          
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={10} width="100%">
            {coreValues.map((value, index) => (
              <VStack
                key={index}
                p={8}
                bg="white"
                borderRadius="xl"
                boxShadow="lg"
                border="2px solid"
                borderColor="gray.200"
                spacing={4}
                transition="all 0.3s ease"
                _hover={{ transform: "translateY(-10px)", boxShadow: "2xl", borderColor: value.color }}
              >
                <Icon as={value.icon} boxSize={16} color={value.color} />
                <Heading fontSize="1.8rem" textAlign="center">
                  {value.title}
                </Heading>
                <Text color="gray.600" textAlign="center" lineHeight="1.8">
                  {value.description}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </VStack>
      </FullScreenSection>

      {/* Team Section */}
      <FullScreenSection
        justifyContent="center"
        alignItems="center"
        isDarkBackground={false}
        backgroundColor="gray.50"
        py={100}
      >
        <VStack spacing={12} maxW="1200px">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaUsers} boxSize={16} color="black.600" />
            <Heading fontSize="3rem" fontWeight="bold">
              {/*Meet Our Team*/}
            </Heading>
            <Text fontSize="1.2rem" color="gray.600" maxW="800px" lineHeight="1.8">
              Our team is led by Precious Diyokeugwu (CEO & Founder), Chinedu Cyril (Head of Marketing), and other experienced professionals from fintech, technology, and financial services.
              Our diverse team is united by a common goal: making cross-border payments accessible and affordable for everyone. Our commitments also include charity partnerships that support the communities we serve.
            </Text>
          </VStack>

         {/*} <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} width="100%">
            {teamMembers.map((member, index) => (
              <VStack
                key={index}
                bg="white"
                borderRadius="xl"
                boxShadow="lg"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{ transform: "translateY(-10px)", boxShadow: "2xl" }}
              >
                <Image 
                  src={member.image} 
                  alt={member.name}
                  width="100%"
                  height="250px"
                  objectFit="cover"
                />
                <VStack p={6} spacing={2} align="center">
                  <Heading fontSize="1.3rem">{member.name}</Heading>
                  <Text color="blue.600" fontWeight="semibold">{member.role}</Text>
                  <Text color="gray.600" fontSize="sm" textAlign="center">{member.bio}</Text>
                </VStack>
              </VStack>
            ))}
          </SimpleGrid> */}

          <Box p={8} bg="blue.50" borderRadius="xl" maxW="900px" textAlign="center">
            <Text fontSize="1.2rem" color="gray.700" lineHeight="1.8">
              Beyond our leadership team, we have a dedicated group of engineers, customer support specialists, 
              and compliance experts working tirelessly to deliver the best possible experience for our users. We're proud 
              to be a diverse, inclusive team representing the communities we serve.
            </Text>
          </Box>
        </VStack>
      </FullScreenSection>

      {/* Call to Action */}
     
      <JoinCampaign />
    
   
    </VStack>
  );
};

export default AboutUs;
